// src/pages/Languages.js
//
// Admin CRUD for the languages a user can pick from (see internal/models/
// localization.go's own doc comment on Language, and internal/config/
// seed_languages.go for the day-one built-ins: English default + Arabic).
// Gated by language_view/create/edit/delete — the same permissions the
// backend's already-wired routes used before this page existed (see
// internal/routes/routes.go's "/Localization/languages" group).
//
// A single inline modal (not a separate Add/Edit page — Language only has a
// handful of fields) covers both create and edit; deleting a language is
// blocked server-side while it's the default or still someone's preferred
// language (see DeleteLanguageHandler's own doc comment), surfaced here as
// a plain error toast rather than re-implemented client-side.
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getLanguages, createLanguage, updateLanguage, deleteLanguage } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";

const DIRECTION_OPTIONS = [
  { value: "ltr", label: "LTR — Left to Right" },
  { value: "rtl", label: "RTL — Right to Left" },
];

const emptyForm = { name: "", code: "", direction: "ltr", is_active: true, is_default: false, sort_order: 0 };

export default function Languages() {
  const [languages, setLanguages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "sort_order", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchLanguages = async () => {
    try {
      const data = await getLanguages(token);
      setLanguages(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchLanguages(); }, []);

  const sortedAndFiltered = useMemo(() => {
    let result = [...languages].filter((l) =>
      l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [languages, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name,
      code: row.code,
      direction: row.direction || "ltr",
      is_active: !!row.is_active,
      is_default: !!row.is_default,
      sort_order: row.sort_order || 0,
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const handleFormSubmit = async () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.code.trim()) errors.code = "Code is required";
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editingId) {
        await updateLanguage(token, editingId, payload);
        notify.success("Language updated successfully");
      } else {
        await createLanguage(token, payload);
        notify.success("Language added successfully");
      }
      setFormOpen(false);
      fetchLanguages();
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to save language");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteLanguage(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success("Language deleted successfully");
      fetchLanguages();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || "Failed to delete language");
    }
  };

  const columns = [
    { key: "id", label: "ID", className: "text-gray-400 font-mono" },
    {
      key: "name",
      label: "Language",
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <ICONS.Geography className="w-4 h-4" />
          </div>
          {row.name}
          {row.is_default && (
            <span className="text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
              Default
            </span>
          )}
        </div>
      ),
    },
    { key: "code", label: "Code", className: "text-gray-600 font-mono uppercase" },
    {
      key: "direction",
      label: "Direction",
      render: (row) => (row.direction === "rtl" ? "RTL" : "LTR"),
    },
    {
      key: "is_active",
      label: "Status",
      render: (row) => (
        <span
          className={`text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full ${
            row.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"
          }`}
        >
          {row.is_active ? "Active" : "Disabled"}
        </span>
      ),
    },
    { key: "sort_order", label: "Order", className: "text-gray-500 font-mono" },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title="Delete Language"
        message="Are you sure? This will fail if it's the default language or still someone's preferred language."
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      {formOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-black text-gray-800 mb-6">
              {editingId ? "Edit Language" : "Add Language"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
              <FloatingInput
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                error={formErrors.name}
                icon={ICONS.Geography}
              />
              <FloatingInput
                label="Code (e.g. en, ar, fr)"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.trim().toLowerCase() })}
                error={formErrors.code}
                icon={ICONS.Countries}
              />
            </div>

            <SelectBox
              label="Text Direction"
              name="direction"
              value={form.direction}
              onChange={(val) => setForm({ ...form, direction: val })}
              options={DIRECTION_OPTIONS}
            />

            <FloatingInput
              label="Sort Order"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            />

            <ToggleSwitch
              label="Active (selectable by users)"
              name="is_active"
              checked={form.is_active}
              onChange={(val) => setForm({ ...form, is_active: val })}
            />
            <ToggleSwitch
              label="Default language (English fallback)"
              name="is_default"
              checked={form.is_default}
              onChange={(val) => setForm({ ...form, is_default: val })}
            />

            <div className="flex gap-3 mt-8">
              <Button variant="secondary" className="flex-1" onClick={() => setFormOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" className="flex-1" onClick={handleFormSubmit} isLoading={saving}>
                {editingId ? "Save Changes" : "Add Language"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Geography size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>Languages</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">Languages users can pick from, and their text direction</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder="Search languages..."
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={openCreate} className="w-full sm:w-auto shrink-0">
            Add Language
          </Button>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => openEdit(row)}>{isMobile && " Edit"}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && " Delete"}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">#{row.id}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
